import React, { useState, useContext } from 'react';
import { Button, Col, Form, Input, Label, Row, UncontrolledAlert, Card, CardBody, Modal, FormGroup } from 'reactstrap'
import ApptSysContext from './ApptSysContext';
import LoadingOverlay from 'react-loading-overlay';
import Error from 'pages/NotificationModal/Error';
import Success from './Success'
import Confirmation from './Confirmation';

const SQS_PREFIX = 'https://sqs.us-east-1.amazonaws.com/';
// TODO(confirm): verify which account is Qued vs Freightpins (defaulted by usage frequency).
const QUEUE_ENVIRONMENTS = [
  { label: 'Qued', account: '815159880286' },
  { label: 'Freightpins', account: '586794470687' },
];
const INTEG_TYPES = ['WEB', 'API', 'EMAIL'];
// Underlying values must stay string/list (meta) and text/password (cred); labels are just friendlier.
const META_FIELD_TYPES = [
  { value: 'string', label: 'Text' },
  { value: 'list', label: 'List' },
];
const CRED_FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'password', label: 'Password' },
];

// Derive a default queue name from the system name, e.g. "BLUE YONDER" -> "appt_req_blueyonder_q".
// Fully editable afterwards to support .fifo and one-off names.
const deriveQueueName = (systemName) => {
  const slug = (systemName || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  return slug ? `appt_req_${slug}_q` : '';
};

// Split an existing SQS queue URL back into account id + queue name for edit mode.
const parseReqQueue = (url) => {
  const match = (url || '').match(/amazonaws\.com\/(\d+)\/(.+)$/);
  return match ? { account_id: match[1], queue_name: match[2] } : { account_id: '', queue_name: '' };
};

const parseJsonMaybe = (value) => {
  if (!value) return null;
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch (e) {
    // Some rows are stored Python-dict style (single quotes, True/False/None) — normalize then retry.
    try {
      const normalized = value
        .replace(/'/g, '"')
        .replace(/\bTrue\b/g, 'true')
        .replace(/\bFalse\b/g, 'false')
        .replace(/\bNone\b/g, 'null');
      return JSON.parse(normalized);
    } catch (e2) {
      return null;
    }
  }
};

const parseConfigFields = (cfg) => {
  const parsed = parseJsonMaybe(cfg);
  return parsed && Array.isArray(parsed.fields) ? parsed.fields : [];
};

const ApptSysForm = ({ hideForm, editSystem }) => {
  const { loading, success, error, setSuccess, setError, submitCreate, submitUpdate, createResponse, errorMessage } = useContext(ApptSysContext)
  const isEdit = !!editSystem;
  const [showConfirmation, setShowConfirmation] = useState(false);

  const defaultForm = {
    system_nm: '',
    integ_type: '',
    appt_sys_scope: '1',
    system_grp: '',
    account_id: QUEUE_ENVIRONMENTS[0].account,
    queue_name: '',
  };

  const defaultAttrs = {
    sched_portal_urls: [''],
    pickup_track_prefixes: [''],
    cancel_enabled: false,
    appt_time_not_required: false,
  };

  // Hydrate form state from the selected system when editing.
  const buildInitialForm = () => {
    if (!editSystem) return defaultForm;
    const { account_id, queue_name } = parseReqQueue(editSystem.req_queue);
    return {
      system_nm: editSystem.system_nm || '',
      integ_type: editSystem.integ_type || '',
      appt_sys_scope: editSystem.appt_sys_scope != null ? String(editSystem.appt_sys_scope) : '',
      system_grp: editSystem.system_grp || '',
      account_id: account_id || QUEUE_ENVIRONMENTS[0].account,
      queue_name: queue_name || '',
    };
  };

  const buildInitialAttrs = () => {
    const parsed = editSystem ? parseJsonMaybe(editSystem.sys_attrbs) : null;
    if (!parsed) return defaultAttrs;
    return {
      sched_portal_urls: Array.isArray(parsed.sched_portal_urls) && parsed.sched_portal_urls.length ? parsed.sched_portal_urls : [''],
      pickup_track_prefixes: Array.isArray(parsed.pickup_track_prefixes) && parsed.pickup_track_prefixes.length ? parsed.pickup_track_prefixes : [''],
      cancel_enabled: !!parsed.cancel_enabled,
      appt_time_not_required: !!parsed.appt_time_not_required,
    };
  };

  const buildInitialMeta = () =>
    editSystem ? parseConfigFields(editSystem.field_config).map(f => ({ name: f.name || '', type: f.type || 'string', display_name: f.display_name || '' })) : [];

  const buildInitialCred = () =>
    editSystem ? parseConfigFields(editSystem.cred_config).map(f => ({ name: f.name || '', type: f.type || 'text', label: f.label || '' })) : [];

  const [formData, setFormData] = useState(buildInitialForm);
  const [attrs, setAttrs] = useState(buildInitialAttrs);
  const [metaFields, setMetaFields] = useState(buildInitialMeta); // field_config.fields
  const [credFields, setCredFields] = useState(buildInitialCred); // cred_config.fields
  const [queueNameTouched, setQueueNameTouched] = useState(isEdit); // don't auto-derive over an existing queue
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const next = { ...prev, [name]: value };
      // Keep the queue name in sync with the system name until the user edits it directly.
      if (name === 'system_nm' && !queueNameTouched) {
        next.queue_name = deriveQueueName(value);
      }
      return next;
    });
  };

  const handleQueueNameChange = (e) => {
    setQueueNameTouched(true);
    setFormData(prev => ({ ...prev, queue_name: e.target.value }));
  };

  // ---- sys_attrbs list helpers (sched_portal_urls, pickup_track_prefixes) ----
  const updateListItem = (key, idx, value) => {
    setAttrs(prev => {
      const list = [...prev[key]];
      list[idx] = value;
      return { ...prev, [key]: list };
    });
  };

  const addListItem = (key) => {
    setAttrs(prev => ({ ...prev, [key]: [...prev[key], ''] }));
  };

  const removeListItem = (key, idx) => {
    setAttrs(prev => {
      const list = prev[key].filter((_, i) => i !== idx);
      return { ...prev, [key]: list.length ? list : [''] };
    });
  };

  const toggleAttr = (key) => {
    setAttrs(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // ---- field_config / cred_config dynamic field editors ----
  const addConfigRow = (setter, template) => {
    setter(prev => [...prev, { ...template }]);
  };

  const updateConfigRow = (setter, idx, key, value) => {
    setter(prev => prev.map((row, i) => (i === idx ? { ...row, [key]: value } : row)));
  };

  const removeConfigRow = (setter, idx) => {
    setter(prev => prev.filter((_, i) => i !== idx));
  };

  // field_config uses `display_name`; cred_config uses `label`.
  const buildFieldConfig = () => {
    const fields = metaFields
      .filter(f => f.name && f.name.trim())
      .map(f => ({
        name: f.name.trim(),
        type: f.type,
        display_name: (f.display_name || '').trim() || f.name.trim(),
      }));
    return fields.length ? JSON.stringify({ fields }) : null;
  };

  const buildCredConfig = () => {
    const fields = credFields
      .filter(f => f.name && f.name.trim())
      .map(f => ({
        name: f.name.trim(),
        type: f.type,
        label: (f.label || '').trim() || f.name.trim(),
      }));
    return fields.length ? JSON.stringify({ fields }) : null;
  };

  const buildSysAttrbs = () => {
    const out = {};
    const urls = attrs.sched_portal_urls.map(s => s.trim()).filter(Boolean);
    const prefixes = attrs.pickup_track_prefixes.map(s => s.trim()).filter(Boolean);
    if (attrs.cancel_enabled) out.cancel_enabled = true;
    if (urls.length) out.sched_portal_urls = urls;
    if (prefixes.length) out.pickup_track_prefixes = prefixes;
    if (attrs.appt_time_not_required) out.appt_time_not_required = true;
    return Object.keys(out).length ? JSON.stringify(out) : null;
  };

  const handleSubmit = async () => {
    setShowConfirmation(true);
  };

  const handleConfirmSubmit = async () => {
    setShowConfirmation(false);

    const emptyFields = [];
    if (!formData.system_nm || formData.system_nm.trim() === '') {
      emptyFields.push('System Name');
    }
    if (!formData.integ_type || formData.integ_type.trim() === '') {
      emptyFields.push('Integration Type');
    }
    if (!formData.account_id || formData.account_id.trim() === '') {
      emptyFields.push('Queue Account ID');
    }
    if (!formData.queue_name || formData.queue_name.trim() === '') {
      emptyFields.push('Queue Name');
    }
    if (!attrs.sched_portal_urls.some(u => u && u.trim() !== '')) {
      emptyFields.push('Scheduling Portal URLs');
    }

    if (emptyFields.length > 0) {
      setErrorMsg(`Please fill all required fields: ${emptyFields.join(', ')}`);
      return;
    }

    try {
      const reqQueueUrl = `${SQS_PREFIX}${formData.account_id.trim()}/${formData.queue_name.trim()}`;

      const payload = {
        // On create, system_id is derived from the name; on edit it is the existing PK (never re-derived).
        system_id: isEdit ? editSystem.system_id : formData.system_nm,
        system_nm: formData.system_nm,
        integ_type: formData.integ_type,
        appt_sys_scope: formData.appt_sys_scope ? parseInt(formData.appt_sys_scope) : null,
        appt_meta: isEdit ? (editSystem.appt_meta ?? null) : null, // no UI field — preserve existing on edit (full-replace PUT)
        req_queue: reqQueueUrl,
        system_grp: formData.system_grp.trim() || formData.system_nm, // defaults to system name
        sys_attrbs: buildSysAttrbs(),
        field_config: buildFieldConfig(),
        cred_config: buildCredConfig(),
      };

      if (isEdit) {
        await submitUpdate(payload);
      } else {
        await submitCreate(payload);
      }
      setErrorMsg('');
    } catch (error) {
      setErrorMsg(`Error: ${error.message}`);
    }
  };

  const handleCancel = () => {
    setShowConfirmation(false);
    hideForm();
  };

  const closeSuccess = () => {
    setSuccess(false);
    hideForm();
  };

  const togError = () => {
    setError(false);
  };

  const renderListEditor = (key, label, placeholder) => (
    <Row className="mb-2">
      <Label className="col-sm-3 col-form-label">{label}</Label>
      <Col sm={6} className="ms-4">
        {attrs[key].map((val, idx) => (
          <div className="d-flex gap-2 mb-2" key={idx}>
            <Input
              type="text"
              value={val}
              onChange={(e) => updateListItem(key, idx, e.target.value)}
              className="form-control"
              placeholder={placeholder}
            />
            <Button
              type="button"
              color="light"
              className="btn"
              onClick={() => removeListItem(key, idx)}
              title="Remove"
            >
              &times;
            </Button>
          </div>
        ))}
        <Button type="button" color="link" className="p-0" onClick={() => addListItem(key)}>
          + Add
        </Button>
      </Col>
    </Row>
  );

  // Editor for a list of {name, type, label/display_name} field definitions.
  const renderConfigEditor = (rows, setter, { labelKey, labelText, types, template, sectionLabel, addLabel, example }) => (
    <Row className="mb-2">
      <Label className="col-sm-3 col-form-label">{sectionLabel}</Label>
      <Col sm={8} className="ms-4">
        <div className="mb-2">
          <small className="text-muted fst-italic">
            Example: {example.label} (what it looks like) &middot; <span style={{ fontFamily: 'monospace' }}>{example.name}</span> (actual key name) &middot; {example.typeLabel}
          </small>
        </div>
        {rows.length > 0 && (
          <div className="d-flex gap-2 mb-1">
            <small className="text-muted" style={{ flex: 1 }}>What it looks like</small>
            <small className="text-muted" style={{ flex: 1 }}>Actual key name</small>
            <small className="text-muted" style={{ width: '110px' }}>Type</small>
            <span style={{ width: '32px' }} />
          </div>
        )}
        {rows.map((row, idx) => (
          <div className="d-flex gap-2 mb-2" key={idx}>
            <Input
              type="text"
              value={row[labelKey] || ''}
              onChange={(e) => updateConfigRow(setter, idx, labelKey, e.target.value)}
              className="form-control"
              placeholder={example.label}
              style={{ flex: 1 }}
            />
            <Input
              type="text"
              value={row.name || ''}
              onChange={(e) => updateConfigRow(setter, idx, 'name', e.target.value)}
              className="form-control"
              placeholder={example.name}
              style={{ flex: 1, fontFamily: 'monospace', fontSize: '13px' }}
            />
            <Input
              type="select"
              value={row.type}
              onChange={(e) => updateConfigRow(setter, idx, 'type', e.target.value)}
              className="form-control"
              style={{ width: '110px' }}
            >
              {types.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </Input>
            <Button type="button" color="light" className="btn" onClick={() => removeConfigRow(setter, idx)} title="Remove" style={{ width: '32px' }}>
              &times;
            </Button>
          </div>
        ))}
        <Button type="button" color="link" className="p-0" onClick={() => addConfigRow(setter, template)}>
          {addLabel}
        </Button>
      </Col>
    </Row>
  );

  // Show the known environments; if an existing system points at another account, surface it as its own option.
  const envOptions = QUEUE_ENVIRONMENTS.some(e => e.account === formData.account_id)
    ? QUEUE_ENVIRONMENTS
    : [...QUEUE_ENVIRONMENTS, { label: formData.account_id, account: formData.account_id }];

  return (
    <>
      <Modal isOpen={success} toggle={() => { closeSuccess() }} centered size="md">
        <Success
          data={createResponse}
          cancel={closeSuccess}
        />
      </Modal>

      <Modal isOpen={error} toggle={() => { togError() }} centered size="sm">
        <Error
          message={errorMessage || 'Something went wrong'}
          closeModal={togError}
        />
      </Modal>

      <Modal isOpen={showConfirmation} toggle={() => setShowConfirmation(false)} centered size="sm">
        <Confirmation
          message={isEdit ? 'Do you want to update this system?' : 'Do you want to create this system?'}
          confirm={handleConfirmSubmit}
          cancel={() => setShowConfirmation(false)}
        />
      </Modal>

      <LoadingOverlay active={loading} spinner>
        <Card className='p-3'>
          <div className="mb-3">
            <h6 className='text-primary'>{isEdit ? 'Edit Appointment System' : 'Add New Appointment System'}</h6>
          </div>
          <CardBody className="p-0">
            <Card outline color="primary" className="border mt-2 mb-3">
              <CardBody className="p-3">
                {errorMsg && (
                  <div className="mb-3">
                    <UncontrolledAlert color="danger" role="alert">
                      {errorMsg}
                    </UncontrolledAlert>
                  </div>
                )}

                <Form>
                  <Row className="mb-2">
                    <Label className="col-sm-3 col-form-label">System Name *</Label>
                    <Col sm={6} className="ms-4">
                      <Input type="text" name="system_nm" value={formData.system_nm} onChange={handleChange} className="form-control" placeholder="Enter system name"
                        disabled={isEdit}
                      />
                      {isEdit && <small className="text-muted">System name is the identifier and cannot be changed.</small>}
                    </Col>
                  </Row>

                  <Row className="mb-2">
                    <Label className="col-sm-3 col-form-label">Integration Type *</Label>
                    <Col sm={6} className="ms-4">
                      <Input type="select" name="integ_type" value={formData.integ_type} onChange={handleChange} className="form-control">
                        <option value="">Select integration type</option>
                        {INTEG_TYPES.map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </Input>
                    </Col>
                  </Row>

                  <Row className="mb-2">
                    <Label className="col-sm-3 col-form-label">Scope</Label>
                    <Col sm={6} className="ms-4">
                      <Input type="number" name="appt_sys_scope" value={formData.appt_sys_scope} onChange={handleChange} className="form-control" placeholder="Enter scope"
                      />
                    </Col>
                  </Row>

                  <Row className="mb-2">
                    <Label className="col-sm-3 col-form-label">Environment *</Label>
                    <Col sm={6} className="ms-4">
                      <Input type="select" name="account_id" value={formData.account_id} onChange={handleChange} className="form-control">
                        {envOptions.map(e => (
                          <option key={e.account} value={e.account}>{e.label}</option>
                        ))}
                      </Input>
                    </Col>
                  </Row>

                  <Row className="mb-2">
                    <Label className="col-sm-3 col-form-label">Queue Name *</Label>
                    <Col sm={6} className="ms-4">
                      <Input
                        type="text"
                        name="queue_name"
                        value={formData.queue_name}
                        onChange={handleQueueNameChange}
                        className="form-control"
                        style={{ fontFamily: 'monospace', fontSize: '13px' }}
                        placeholder="appt_req_{name}_q"
                      />
                      <small className="text-muted">Auto-filled from the system name. Edit only if needed (e.g. .fifo queues).</small>
                    </Col>
                  </Row>

                  <Row className="mb-2">
                    <Label className="col-sm-3 col-form-label">System Group</Label>
                    <Col sm={6} className="ms-4">
                      <Input type="text" name="system_grp" value={formData.system_grp} onChange={handleChange} className="form-control" placeholder="Defaults to system name"
                      />
                    </Col>
                  </Row>

                  <hr className="my-3" />
                  <div className="mb-2">
                    <small className="text-muted">System Attributes</small>
                  </div>

                  {renderListEditor('sched_portal_urls', 'Scheduling Portal URLs *', 'e.g. app.fourkites.com')}
                  {renderListEditor('pickup_track_prefixes', 'Pickup Track Prefixes', 'e.g. MAEU')}

                  <Row className="mb-2">
                    <Label className="col-sm-3 col-form-label">Flags</Label>
                    <Col sm={6} className="ms-4">
                      <FormGroup check className="mb-1">
                        <Input type="checkbox" id="cancel_enabled" checked={attrs.cancel_enabled} onChange={() => toggleAttr('cancel_enabled')} />
                        <Label check for="cancel_enabled">Cancel enabled</Label>
                      </FormGroup>
                      <FormGroup check>
                        <Input type="checkbox" id="appt_time_not_required" checked={attrs.appt_time_not_required} onChange={() => toggleAttr('appt_time_not_required')} />
                        <Label check for="appt_time_not_required">Appointment time not required</Label>
                      </FormGroup>
                    </Col>
                  </Row>

                  <hr className="my-3" />
                  <div className="mb-2">
                    <small className="text-muted">Meta Fields</small>
                    <div><small className="text-muted">Identifiers this system needs to look up a location (e.g. OPENDOCK uses a Warehouse Id, RETALIX uses a Company Id).</small></div>
                  </div>
                  {renderConfigEditor(metaFields, setMetaFields, {
                    labelKey: 'display_name',
                    labelText: 'Display name',
                    types: META_FIELD_TYPES,
                    template: { name: '', type: 'string', display_name: '' },
                    sectionLabel: 'Meta Fields',
                    addLabel: '+ Add meta field',
                    example: { label: 'Warehouse Id', name: 'warehouseId', typeLabel: 'Text' },
                  })}

                  <hr className="my-3" />
                  <div className="mb-2">
                    <small className="text-muted">Credential Fields</small>
                    <div><small className="text-muted">Leave empty to use the default username / password. Only add fields if this system needs different credentials (e.g. UBERFREIGHTAPI uses clientId / clientSecret).</small></div>
                  </div>
                  {renderConfigEditor(credFields, setCredFields, {
                    labelKey: 'label',
                    labelText: 'Label',
                    types: CRED_FIELD_TYPES,
                    template: { name: '', type: 'text', label: '' },
                    sectionLabel: 'Credential Fields',
                    addLabel: '+ Add credential field',
                    example: { label: 'Client ID', name: 'clientId', typeLabel: 'Password' },
                  })}
                </Form>
              </CardBody>
            </Card>

            <div className="d-flex justify-content-end mt-3 me-4">
              <div className='d-flex gap-2'>
                <Button onClick={handleSubmit} className='btn' color='primary'>
                  {isEdit ? 'Update' : 'Create'}
                </Button>
                <Button onClick={handleCancel} className='btn' color='secondary'>
                  Cancel
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      </LoadingOverlay>
    </>
  );
};

export default ApptSysForm;
